import type { ReactNode } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  AlertCircle,
  AlertTriangle,
  Calendar,
  CheckCircle2,
  ChevronLeft,
  Loader2,
  Package,
  Palette,
  Pencil,
  Store,
  Tag,
} from "lucide-react";

import type { Pro014StepFourItem } from "@/@types/campaign";
import type { CampaignConflictItem, ConflictingCampaign } from "@/@types/campaign-conflict";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { formatBRL } from "@/utils/money";

import { describeConflictingCampaign } from "../../../utils/conflict-scope";
import { parseDateStringLocal } from "../../../utils/date-utils";
import {
  DATE_LONG_FORMAT,
  PRO014_DISCOUNT_TYPE_LABELS,
  PRO014_PAYMENT_METHOD_LABELS,
  PRO014_REVIEW_CONFLICTS_PREVIEW,
  PRO014_REVIEW_ITEMS_PREVIEW,
  PRO014_REVIEW_STATUS,
  PRO014_REVIEW_TEXTS,
  PRO014_SUBMIT_STATE,
  PRO014_WIZARD_MODE,
  PRO014_WIZARD_TEXTS,
} from "../../constants";
import type {
  Pro014CampaignData,
  Pro014ReviewStatus,
  Pro014SubmitState,
  Pro014WizardMode,
  ValidationProgress,
} from "../../types";

interface StepFiveProps {
  data: Pro014CampaignData;
  prevStep: () => void;
  onSubmit: () => Promise<void>;
  isLoading: boolean;
  mode?: Pro014WizardMode;
  conflicts?: CampaignConflictItem[];
  onEditProducts?: () => void;
  validationProgress?: ValidationProgress | null;
}

interface ReviewCardProps {
  icon: ReactNode;
  title: string;
  children: ReactNode;
}

interface ReviewStatusStyle {
  box: string;
  title: string;
  text: string;
}

const REVIEW_STATUS_STYLE: Record<Pro014ReviewStatus, ReviewStatusStyle> = {
  [PRO014_REVIEW_STATUS.READY]: {
    box: "bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-900 rounded-lg p-3",
    title: "text-green-800 dark:text-green-300",
    text: "text-green-700 dark:text-green-400",
  },
  [PRO014_REVIEW_STATUS.CONFLICTS]: {
    box: "bg-yellow-50 dark:bg-yellow-950/20 border border-yellow-200 dark:border-yellow-900 rounded-lg p-3",
    title: "text-yellow-800 dark:text-yellow-300",
    text: "text-yellow-700 dark:text-yellow-400",
  },
};

const REVIEW_STATUS_ICON: Record<Pro014ReviewStatus, () => JSX.Element> = {
  [PRO014_REVIEW_STATUS.READY]: () => (
    <CheckCircle2 className="mt-0.5 h-4 w-4 flex-shrink-0 text-green-600 dark:text-green-500" aria-hidden />
  ),
  [PRO014_REVIEW_STATUS.CONFLICTS]: () => (
    <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-yellow-600 dark:text-yellow-500" aria-hidden />
  ),
};

const REVIEW_STATUS_TITLE: Record<Pro014ReviewStatus, string> = {
  [PRO014_REVIEW_STATUS.READY]: PRO014_REVIEW_TEXTS.READY_TITLE,
  [PRO014_REVIEW_STATUS.CONFLICTS]: PRO014_REVIEW_TEXTS.CONFLICTS_TITLE,
};

const formatLongDate = (dateString: string): string => {
  try {
    return format(parseDateStringLocal(dateString), DATE_LONG_FORMAT, { locale: ptBR });
  } catch {
    return dateString;
  }
};

const submitStateOf = (isValidating: boolean, isLoading: boolean): Pro014SubmitState => {
  if (isValidating) return PRO014_SUBMIT_STATE.VALIDATING;
  if (isLoading) return PRO014_SUBMIT_STATE.SAVING;
  return PRO014_SUBMIT_STATE.IDLE;
};

function ReviewCard({ icon, title, children }: ReviewCardProps): JSX.Element {
  return (
    <div className="space-y-3 rounded-lg border bg-card p-3 transition-colors hover:border-primary/50">
      <div className="flex items-center gap-2">
        <div className="rounded-full bg-primary/10 p-1.5">{icon}</div>
        <h3 className="text-sm font-semibold">{title}</h3>
      </div>
      {children}
    </div>
  );
}

function ReviewField({ label, value }: { label: string; value: ReactNode }): JSX.Element {
  return (
    <div className="grid gap-0.5">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-sm font-medium">{value}</p>
    </div>
  );
}

export default function StepFive({
  data,
  prevStep,
  onSubmit,
  isLoading,
  mode = PRO014_WIZARD_MODE.CREATE,
  conflicts = [],
  onEditProducts,
  validationProgress = null,
}: StepFiveProps): JSX.Element {
  const isValidating: boolean = validationProgress !== null;
  const submitState: Pro014SubmitState = submitStateOf(isValidating, isLoading);
  const reviewStatus: Pro014ReviewStatus =
    conflicts.length > 0 ? PRO014_REVIEW_STATUS.CONFLICTS : PRO014_REVIEW_STATUS.READY;
  const statusStyle: ReviewStatusStyle = REVIEW_STATUS_STYLE[reviewStatus];
  const statusDescription: Record<Pro014ReviewStatus, string> = {
    [PRO014_REVIEW_STATUS.READY]: PRO014_REVIEW_TEXTS.READY_DESCRIPTION(
      PRO014_REVIEW_TEXTS.SUBMIT_LABEL[mode],
    ),
    [PRO014_REVIEW_STATUS.CONFLICTS]: PRO014_REVIEW_TEXTS.CONFLICTS_DESCRIPTION(conflicts.length),
  };
  const storesLabel: string = data.allStores
    ? PRO014_REVIEW_TEXTS.ALL_STORES
    : PRO014_REVIEW_TEXTS.SPECIFIC_STORES(data.stores.length);
  const restrictionLabel: string = data.gradeRestricted
    ? PRO014_REVIEW_TEXTS.RESTRICTION_ON(data.items.length)
    : PRO014_REVIEW_TEXTS.RESTRICTION_OFF;
  const hiddenItems: number = data.items.length - PRO014_REVIEW_ITEMS_PREVIEW;
  const hiddenConflicts: number = conflicts.length - PRO014_REVIEW_CONFLICTS_PREVIEW;

  const submitContent: Record<Pro014SubmitState, ReactNode> = {
    [PRO014_SUBMIT_STATE.VALIDATING]: (
      <>
        <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
        {PRO014_REVIEW_TEXTS.VALIDATING}
      </>
    ),
    [PRO014_SUBMIT_STATE.SAVING]: (
      <>
        <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
        {PRO014_REVIEW_TEXTS.SUBMITTING_LABEL[mode]}
      </>
    ),
    [PRO014_SUBMIT_STATE.IDLE]: (
      <>
        <CheckCircle2 className="mr-2 h-4 w-4" aria-hidden />
        {PRO014_REVIEW_TEXTS.SUBMIT_LABEL[mode]}
      </>
    ),
  };

  return (
    <div className="space-y-4 animate-in fade-in duration-300">
      <div className="space-y-3">
        <ReviewCard
          icon={<Tag className="h-4 w-4 text-primary" aria-hidden />}
          title={PRO014_REVIEW_TEXTS.BASIC_INFO}
        >
          <div className="grid gap-3 pl-1">
            <ReviewField label={PRO014_REVIEW_TEXTS.DESCRIPTION} value={data.description} />
          </div>
        </ReviewCard>

        <ReviewCard
          icon={<Calendar className="h-4 w-4 text-primary" aria-hidden />}
          title={PRO014_REVIEW_TEXTS.PERIOD}
        >
          <div className="grid gap-3 pl-1 md:grid-cols-2">
            <ReviewField label={PRO014_REVIEW_TEXTS.START_DATE} value={formatLongDate(data.startDate)} />
            <ReviewField label={PRO014_REVIEW_TEXTS.END_DATE} value={formatLongDate(data.endDate)} />
          </div>
        </ReviewCard>

        <ReviewCard
          icon={<Store className="h-4 w-4 text-primary" aria-hidden />}
          title={PRO014_REVIEW_TEXTS.STORES_TITLE}
        >
          <div className="pl-1">
            <ReviewField label={PRO014_REVIEW_TEXTS.STORES_SCOPE} value={storesLabel} />
          </div>
        </ReviewCard>

        <ReviewCard
          icon={<Palette className="h-4 w-4 text-primary" aria-hidden />}
          title={PRO014_REVIEW_TEXTS.RESTRICTION_TITLE}
        >
          <p className="pl-1 text-sm font-medium" data-testid="review-grade-restriction">
            {restrictionLabel}
          </p>
        </ReviewCard>

        <ReviewCard
          icon={<Tag className="h-4 w-4 text-primary" aria-hidden />}
          title={PRO014_REVIEW_TEXTS.DISCOUNT_TITLE}
        >
          <div className="grid gap-3 pl-1">
            <ReviewField
              label={PRO014_REVIEW_TEXTS.DISCOUNT_TYPE}
              value={
                PRO014_DISCOUNT_TYPE_LABELS[data.discountType] ??
                PRO014_REVIEW_TEXTS.DISCOUNT_TYPE_FALLBACK(data.discountType)
              }
            />
            <div className="grid grid-cols-2 gap-3">
              <ReviewField
                label={PRO014_REVIEW_TEXTS.TAKE_PAY}
                value={PRO014_REVIEW_TEXTS.TAKE_PAY_VALUE(data.takeQuantity, data.payQuantity)}
              />
              <ReviewField label={PRO014_REVIEW_TEXTS.DISCOUNT_VALUE} value={data.discountValue} />
            </div>
            <ReviewField
              label={PRO014_REVIEW_TEXTS.PAYMENT_METHOD}
              value={
                PRO014_PAYMENT_METHOD_LABELS[data.paymentMethod] ??
                PRO014_REVIEW_TEXTS.PAYMENT_METHOD_FALLBACK
              }
            />
            {data.baseValue > 0 && (
              <ReviewField label={PRO014_REVIEW_TEXTS.BASE_VALUE} value={formatBRL(data.baseValue)} />
            )}
          </div>
        </ReviewCard>

        <ReviewCard
          icon={<Package className="h-4 w-4 text-primary" aria-hidden />}
          title={PRO014_REVIEW_TEXTS.PRODUCTS(data.items.length)}
        >
          <div className="max-h-32 overflow-y-auto pl-1">
            <div className="flex flex-wrap gap-1">
              {data.items.slice(0, PRO014_REVIEW_ITEMS_PREVIEW).map((item: Pro014StepFourItem) => (
                <span
                  key={item.sku}
                  className="flex items-center gap-1 rounded bg-secondary px-2 py-1 text-xs"
                  title={item.name || undefined}
                >
                  <span className="font-mono">{item.sku}</span>
                  {item.name && (
                    <span className="text-muted-foreground">
                      {PRO014_WIZARD_TEXTS.ITEM_NAME(item.name)}
                    </span>
                  )}
                </span>
              ))}
              {hiddenItems > 0 && (
                <span className="rounded bg-muted px-2 py-1 text-xs text-muted-foreground">
                  {PRO014_REVIEW_TEXTS.MORE_PRODUCTS(hiddenItems)}
                </span>
              )}
            </div>
          </div>
        </ReviewCard>
      </div>

      {conflicts.length > 0 && (
        <div className="space-y-3 rounded-lg border border-destructive/50 p-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-destructive" aria-hidden />
              <h3 className="text-sm font-semibold text-destructive">
                {PRO014_REVIEW_TEXTS.CONFLICTS_COUNT(conflicts.length)}
              </h3>
            </div>
            {onEditProducts && (
              <Button variant="outline" size="sm" onClick={onEditProducts}>
                <Pencil className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                {PRO014_REVIEW_TEXTS.EDIT_PRODUCTS}
              </Button>
            )}
          </div>
          <div className="max-h-48 space-y-2 overflow-y-auto">
            {conflicts.slice(0, PRO014_REVIEW_CONFLICTS_PREVIEW).map((conflict) => (
              <div key={conflict.itemCode} className="space-y-1 rounded border p-2">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono text-sm font-medium">{conflict.itemCode}</span>
                  {conflict.itemName && (
                    <span className="text-xs text-muted-foreground">{conflict.itemName}</span>
                  )}
                </div>
                <div className="flex flex-wrap gap-1">
                  {conflict.conflictingCampaigns.map((campaign: ConflictingCampaign, index: number) => (
                    <Badge
                      key={`${campaign.code}-${index}`}
                      variant="destructive"
                      className="max-w-full whitespace-normal break-words text-xs"
                    >
                      {describeConflictingCampaign(campaign)}
                    </Badge>
                  ))}
                </div>
              </div>
            ))}
            {hiddenConflicts > 0 && (
              <p className="py-1 text-center text-xs text-muted-foreground">
                {PRO014_REVIEW_TEXTS.MORE_CONFLICTS(hiddenConflicts)}
              </p>
            )}
          </div>
        </div>
      )}

      {validationProgress && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 dark:border-blue-900 dark:bg-blue-950/20">
          <div className="flex items-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin text-blue-600" aria-hidden />
            <p className="text-xs text-blue-900 dark:text-blue-300">
              {PRO014_REVIEW_TEXTS.VALIDATING_BATCH(validationProgress.current, validationProgress.total)}
            </p>
          </div>
        </div>
      )}

      <div className={statusStyle.box}>
        <div className="flex gap-2">
          {REVIEW_STATUS_ICON[reviewStatus]()}
          <div>
            <p className={`mb-1 text-xs font-semibold ${statusStyle.title}`}>
              {REVIEW_STATUS_TITLE[reviewStatus]}
            </p>
            <p className={`text-xs ${statusStyle.text}`}>
              {PRO014_REVIEW_TEXTS.STATUS_SUMMARY(
                statusDescription[reviewStatus],
                PRO014_REVIEW_TEXTS.ITEMS_SUMMARY(data.items.length),
              )}
            </p>
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-3 dark:border-yellow-900 dark:bg-yellow-950/20">
        <div className="flex gap-2">
          <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-yellow-600 dark:text-yellow-500" aria-hidden />
          <div>
            <p className="mb-1 text-xs font-semibold text-yellow-800 dark:text-yellow-300">
              {PRO014_REVIEW_TEXTS.VALIDATION_TITLE}
            </p>
            <p className="text-xs text-yellow-700 dark:text-yellow-400">
              {PRO014_REVIEW_TEXTS.VALIDATION_DESCRIPTION}
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-col-reverse justify-between gap-2 pt-2 md:flex-row">
        <Button
          variant="outline"
          onClick={prevStep}
          disabled={isLoading || isValidating}
          className="w-full md:w-auto"
        >
          <ChevronLeft className="mr-2 h-4 w-4" aria-hidden />
          {PRO014_WIZARD_TEXTS.BACK}
        </Button>
        <Button
          onClick={() => void onSubmit()}
          disabled={isLoading || isValidating || conflicts.length > 0}
          className="w-full md:w-auto"
        >
          {submitContent[submitState]}
        </Button>
      </div>
    </div>
  );
}
